import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    Card,
    CardContent,
    Avatar,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import PeopleIcon from '@mui/icons-material/People';
import RegistrationLinkGenerator from './RegistrationLinkGenerator';

export default function GuestRegistrationsList({
    guestRegistrations,
    onMessageGuests,
    onAddGuest,
    onDeleteGuest,
    classId,
    affiliateId,
    classData,
    subdomain,
    userRole
}) {
    const [isAddGuestModalOpen, setAddGuestModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [guestToDelete, setGuestToDelete] = useState(null);
    const [isLinkGeneratorOpen, setLinkGeneratorOpen] = useState(false);
    
    // Add guest form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [addGuestError, setAddGuestError] = useState('');
    const [isAddingGuest, setIsAddingGuest] = useState(false);

    const handleOpenAddGuest = () => {
        setAddGuestModalOpen(true);
        setAddGuestError('');
    };

    const handleCloseAddGuest = () => {
        setAddGuestModalOpen(false);
        setFirstName('');
        setLastName('');
        setEmail('');
        setAddGuestError('');
    };

    const handleAddGuest = async () => {
        setAddGuestError('');

        // Validation
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            setAddGuestError('All fields are required');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setAddGuestError('Please enter a valid email address');
            return;
        }

        setIsAddingGuest(true);

        try {
            await onAddGuest({
                classId,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                affiliateId
            });
            handleCloseAddGuest();
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to add guest';
            setAddGuestError(errorMessage);
        } finally {
            setIsAddingGuest(false);
        }
    };

    const handleOpenDeleteConfirm = (guest) => {
        setGuestToDelete(guest);
        setDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setGuestToDelete(null);
    };

    const handleDeleteGuest = async () => {
        if (!guestToDelete) return;

        try {
            await onDeleteGuest(guestToDelete.id);
            handleCloseDeleteConfirm();
        } catch (error) {
            console.error('Error deleting guest:', error);
        }
    };

    return (
        <>
            <Box sx={{ mt: 3, mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid',
                        borderColor: 'success.main'
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            color: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        {classData?.freeClass ? <CardGiftcardIcon /> : <PeopleIcon />}
                        Guest
                        <Chip
                            label={guestRegistrations?.length || 0}
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                        />
                    </Typography>
                    
                    {/* Action buttons */}
                    <Box display="flex" alignItems="center" gap={1}>
                        {/* Generate Registration URL button - ainult freeClass puhul */}
                        {classData?.freeClass && (
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                startIcon={<LinkIcon/>}
                                onClick={() => setLinkGeneratorOpen(true)}
                            >
                                Link
                            </Button>
                        )}

                        {/* Add Guest button */}
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<PersonAddIcon/>}
                            onClick={handleOpenAddGuest}
                        >
                            Add Guest
                        </Button>

                        {/* Message button for guests */}
                        {guestRegistrations?.length > 0 && (
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<SendIcon/>}
                                onClick={onMessageGuests}
                            >
                                Message
                            </Button>
                        )}
                    </Box>
                </Box>

                {guestRegistrations?.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5}}>
                        {guestRegistrations.map((guest) => (
                            <Card
                                key={guest.id}
                                elevation={2}
                                sx={{
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    p: 0,
                                    borderLeft: '4px solid',
                                    borderColor: 'success.main'
                                }}
                            >
                                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 2
                                        }}
                                    >
                                        {/* Left side - Guest Info */}
                                        <Box sx={{ flex: 1 }}>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: 'success.main'
                                                    }}
                                                >
                                                    {guest.firstName.charAt(0).toUpperCase()}
                                                </Avatar>

                                                <Box>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {guest.firstName} {guest.lastName}
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary">
                                                        {guest.email}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Typography variant="caption" color="text.secondary">
                                                Registered: {new Date(guest.createdAt).toLocaleString('en-GB', {
                                                    dateStyle: 'short',
                                                    timeStyle: 'short'
                                                })}
                                            </Typography>
                                        </Box>

                                        {/* Right side - Actions */}
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Chip
                                                label="GUEST"
                                                size="small"
                                                color="success"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                            
                                            <IconButton
                                                onClick={() => handleOpenDeleteConfirm(guest)}
                                                size="medium"
                                                color="error"
                                                sx={{
                                                    border: '2px solid',
                                                    borderColor: 'error.main',
                                                    '&:hover': {
                                                        bgcolor: 'error.light',
                                                        color: 'white'
                                                    }
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Add Guest Modal */}
            <Dialog 
                open={isAddGuestModalOpen} 
                onClose={handleCloseAddGuest}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Guest Registration</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            margin="normal"
                            required
                        />

                        <TextField
                            fullWidth
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            margin="normal"
                            required
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            required
                        />

                        {addGuestError && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {addGuestError}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddGuest} disabled={isAddingGuest}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddGuest}
                        variant="contained"
                        color="success"
                        disabled={isAddingGuest || !firstName.trim() || !lastName.trim() || !email.trim()}
                    >
                        {isAddingGuest ? 'Adding...' : 'Add Guest'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCloseDeleteConfirm}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove {guestToDelete?.firstName} {guestToDelete?.lastName} from this class?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteConfirm} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteGuest} color="error">
                        Delete Guest
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Registration Link Generator */}
            <RegistrationLinkGenerator
                open={isLinkGeneratorOpen}
                onClose={() => setLinkGeneratorOpen(false)}
                classData={classData}
                subdomain={subdomain}
            />
        </>
    );
}

